import { API } from '../../apis/limeAuthUtils/apiWrapper';
import { getCurrentAuthUserEmail } from '../../lib/utils';

/**
 * function to list all ddf groups
 * @param requester user email
 * @param region Region selected by user
 * @returns {Promise<any>}
 */
async function fetchGroupsAPI(requester, region) {
    const apiName = `${region}/v1/group-management`;
    const myInit = {
        queryStringParameters: {
            requester: requester,
        },
    };
    return await API.get(apiName, '/v1/groups', myInit);
}

export async function fetchGroups(region) {
    /**
     * function to fetch all ddf groups by calling api
     */
    const authUserEmail = await getCurrentAuthUserEmail();
    return await fetchGroupsAPI(authUserEmail, region);
}

/**
 * function to list devices by email and optional groupID
 * @param requester user email
 * @param region Region selected by user
 * @param groupId optional group ID filter
 * @returns {Promise<any>}
 */
async function fetchDevicesAPI(requester, region, groupId = null) {
    const apiName = `${region}/v1/group-management`;
    const queryParams = { email: requester };

    if (groupId) {
        queryParams.groupId = groupId;
    }

    const myInit = {
        queryStringParameters: queryParams,
    };

    return await API.get(apiName, '/v1/devices', myInit);
}

export async function fetchDevices(region, groupId = null) {
    const authUserEmail = await getCurrentAuthUserEmail();
    return await fetchDevicesAPI(authUserEmail, region, groupId);
}

export async function getUserPrivateGroups(region) {
    const authUserEmail = await getCurrentAuthUserEmail();
    const apiName = `${region}/v1/group-management`;
    const myInit = {
        queryStringParameters: {
            requester: authUserEmail,
            listOwned: 'true'
        },
    };
    return await API.get(apiName, '/v1/groups', myInit);
}

/**
 * function to configure group with customized settings
 * @param groupId group id
 * @param config customized config
 * @param region Region selected by user
 * @returns {Promise<any>}
 */
export async function configureGroup(groupId, config, region) {
    const authUserEmail = await getCurrentAuthUserEmail();
    const apiName = `${region}/v1/group-management`;
    const myInit = {
        body: {
            groupId: groupId,
            requester: authUserEmail,
            config: config,
        },
    };
    return await API.post(apiName, '/v1/groups/update-config', myInit);
}

/**
 * function to configure group with ldap
 * @param groupId group id
 * @param ldap ldap group name
 * @param region Region selected by user
 * @returns {Promise<any>}
 */
export async function configureldap(groupId, ldap, region) {
    /**
     *
     * groupId:
     * ldap:
     */
    const authUserEmail = await getCurrentAuthUserEmail();
    const apiName = `${region}/v1/group-management`;
    const myInit = {
        body: {
            groupId: groupId,
            requester: authUserEmail,
            ldap: ldap,
        },
    };
    return await API.post(apiName, '/v1/groups/configure-ldap', myInit);
}

/**
 * function to create a new ddf group
 * @param groupName group name (e.g. ds2-df-my-team)
 * @param ldap LDAP group name
 * @param region Region selected by user
 * @returns {Promise<any>}
 */
export async function createGroup(groupName, ldap, region) {
    const authUserEmail = await getCurrentAuthUserEmail();
    const apiName = `${region}/v1/group-management`;
    const myInit = {
        body: {
            email: authUserEmail,
            groupName: groupName,
            ldap: ldap,
        },
    };
    return await API.post(apiName, '/v1/groups', myInit);
}

/**
 * function to move devices between groups
 * @param deviceSerials array of device serial numbers to move
 * @param targetGroupId target group ID to move devices to
 * @param region Region selected by user
 * @returns {Promise<any>}
 */
export async function moveDeviceBetweenGroups(deviceSerials, targetGroupId, region) {
    const authUserEmail = await getCurrentAuthUserEmail();
    const apiName = `${region}/v1/group-management`;
    const myInit = {
        body: {
            deviceSerials: deviceSerials,
            email: authUserEmail
        },
    };
    return await API.put(apiName, `/v1/devices/group/${targetGroupId}`, myInit);
}

/**
 * function to delete a ddf group
 * @param groupId group ID to delete
 * @param region Region selected by user
 * @returns {Promise<any>}
 */
export async function deleteGroup(groupId, region) {
    const authUserEmail = await getCurrentAuthUserEmail();
    const apiName = `${region}/v1/group-management`;
    const myInit = {
        body: {
            email: authUserEmail,
        },
    };
    return await API.del(apiName, `/v1/groups/${groupId}`, myInit);
}
